// src/pages/ClientPortalLayout.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    collection, query, where, onSnapshot, doc, getDoc, 
    addDoc, updateDoc, orderBy, runTransaction, serverTimestamp 
} from 'firebase/firestore';
import { db, dbAsset, storage, rtdb } from '../App';
import { appId } from '../config/firebaseConfig';
import { ref as rtdbRef, onValue } from 'firebase/database';
import { EQUIPMENT_CONFIG } from '../config/equipmentConfig';
import PasswordDisplay from '../components/common/PasswordDisplay';
import LinkifyText from '../components/common/LinkifyText';
import TOTPDisplay from '../components/common/TOTPDisplay';
import AssetManagementPage from './AssetManagementPage';
import ClientPortalUsersModal from '../components/ClientPortalUsersModal';
import ClientPortalSidebar from '../components/ClientPortalSidebar';
import SignatureModal from '../components/contracts/SignatureModal';

export default function ClientPortalLayout({ portalUser, onLogout }) {
    const [activeTab, setActiveTab] = useState('tickets'); // 'tickets' | 'company' | 'assets'
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Dados da Empresa
    const [companyData, setCompanyData] = useState(null);
    const [loadingCompany, setLoadingCompany] = useState(true);

    // Chamados
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(true);
    const [ticketFilter, setTicketFilter] = useState('open'); // 'open' | 'closed' | 'all'
    const [searchTerm, setSearchTerm] = useState('');

    // Modal Novo Chamado
    const [isNewTicketOpen, setIsNewTicketOpen] = useState(false);
    const [ticketTitle, setTicketTitle] = useState('');
    const [ticketDescription, setTicketDescription] = useState('');
    const [ticketPriority, setTicketPriority] = useState('low');
    const [ticketContractId, setTicketContractId] = useState('outros');
    const [ticketContractName, setTicketContractName] = useState('Outros');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [submittingTicket, setSubmittingTicket] = useState(false);
    const [ticketFeedback, setTicketFeedback] = useState({ type: '', msg: '' });

    // Detalhe do Chamado
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [commentFiles, setCommentFiles] = useState([]);
    const [sendingComment, setSendingComment] = useState(false);

    // Ativos Monitorados & Equipamentos (Apenas User Company)
    const [assets, setAssets] = useState([]);
    const [loadingAssets, setLoadingAssets] = useState(true);
    const [equipmentList, setEquipmentList] = useState([]);
    const [loadingEquipment, setLoadingEquipment] = useState(true);

    const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
    const [equipmentSearchQueries, setEquipmentSearchQueries] = useState({});
    const [liveData, setLiveData] = useState({});
    const [selectedEquipmentItem, setSelectedEquipmentItem] = useState(null);
    const [assetSubTab, setAssetSubTab] = useState('inventory'); // 'inventory' | 'monitored'

    // Gestão de Contratos (Apenas User Company)
    const [contracts, setContracts] = useState([]);
    const [loadingContracts, setLoadingContracts] = useState(true);
    const [selectedContract, setSelectedContract] = useState(null);
    const [contractVersions, setContractVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [isViewContractModalOpen, setIsViewContractModalOpen] = useState(false);
    const [isSignContractModalOpen, setIsSignContractModalOpen] = useState(false);
    const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);

    // Laudos Técnicos (Apenas User Company)
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(true);
    const [reportSearchTerm, setReportSearchTerm] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    const [isPreviewReportOpen, setIsPreviewReportOpen] = useState(false);

    // Dashboard de Estatísticas
    const [isDashboardOpen, setIsDashboardOpen] = useState(false);
    const [statuses, setStatuses] = useState([]);

    const paths = useMemo(() => ({
        tickets: `/artifacts/${appId}/public/data/tickets`,
        counter: `/artifacts/${appId}/public/data/counters/ticket_counter`,
        contracts: `/artifacts/${appId}/public/data/client-contracts`,
        templates: `/artifacts/${appId}/public/data/contract-templates`,
        reports: `/artifacts/${appId}/public/data/technical_reports`,
        statuses: `/artifacts/${appId}/public/data/ticket_statuses`,
    }), []);

    const [contractTemplates, setContractTemplates] = useState([]);

    // Carregar Modelos Padrões para obter os nomes corretos dos modelos
    useEffect(() => {
        const unsub = onSnapshot(collection(db, paths.templates), (snap) => {
            setContractTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [paths.templates]);

    // 1. Carregar dados da empresa (com fallback entre clients e clients-avulsos)
    useEffect(() => {
        if (!portalUser?.companyId) return;

        const loadCompany = async () => {
            setLoadingCompany(true);
            try {
                const primaryColl = portalUser.companyType || 'clients';
                let snap = await getDoc(doc(db, `/artifacts/${appId}/public/data/${primaryColl}`, portalUser.companyId));

                if (!snap.exists()) {
                    const fallbackColl = primaryColl === 'clients' ? 'clients-avulsos' : 'clients';
                    snap = await getDoc(doc(db, `/artifacts/${appId}/public/data/${fallbackColl}`, portalUser.companyId));
                }

                if (snap.exists()) {
                    setCompanyData({ id: snap.id, ...snap.data() });
                } else {
                    setCompanyData({
                        name: portalUser.companyName,
                        nomeFantasia: portalUser.companyName,
                        telefone: portalUser.phone || '—',
                        email: portalUser.email
                    });
                }
            } catch (err) {
                console.error("Erro ao carregar dados da empresa:", err);
                setCompanyData({
                    name: portalUser.companyName,
                    nomeFantasia: portalUser.companyName,
                    telefone: portalUser.phone || '—',
                    email: portalUser.email
                });
            } finally {
                setLoadingCompany(false);
            }
        };

        loadCompany();
    }, [portalUser?.companyId, portalUser?.companyType, portalUser?.companyName, portalUser?.phone, portalUser?.email]);

    // 2. Carregar chamados com regra de visualização User vs User Company
    useEffect(() => {
        if (!portalUser?.companyId) return;

        const q = query(
            collection(db, paths.tickets),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allTickets = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            // Filtragem de Segurança estrita:
            let visible = [];
            if (portalUser.role === 'User Company') {
                // User Company vê todos os chamados da empresa
                visible = allTickets.filter(t => 
                    String(t.clientCompanyId || '') === String(portalUser.companyId || '') || 
                    (t.clientCompany && portalUser.companyName && t.clientCompany.toLowerCase().trim() === portalUser.companyName.toLowerCase().trim())
                );
            } else {
                // User vê apenas os que ele mesmo abriu
                const userEmailNorm = (portalUser.email || '').toLowerCase().trim();
                const userIdStr = String(portalUser.id || '');
                visible = allTickets.filter(t => 
                    (t.clientUserId && String(t.clientUserId) === userIdStr) ||
                    (t.createdByEmail && t.createdByEmail.toLowerCase().trim() === userEmailNorm) ||
                    (t.clientEmail && t.clientEmail.toLowerCase().trim() === userEmailNorm)
                );
            }

            setTickets(visible);
            setLoadingTickets(false);

            // Atualiza o ticket selecionado se estiver aberto
            if (selectedTicket) {
                const refreshed = visible.find(t => t.id === selectedTicket.id);
                if (refreshed) setSelectedTicket(refreshed);
            }
        }, (error) => {
            console.error("Erro ao carregar chamados:", error);
            setLoadingTickets(false);
        });

        // Carregar configurações de status (para cores e gráficos)
        const unsubStatuses = onSnapshot(query(collection(db, paths.statuses), orderBy('name', 'asc')), (snap) => {
            setStatuses(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            console.warn("Status do ticket não acessíveis ou padrão:", err);
        });

        return () => {
            unsubscribe();
            unsubStatuses();
        };
    }, [portalUser, paths.tickets, paths.statuses, selectedTicket?.id]);

    // 3. Carregar comentários do chamado selecionado (Filtrando isInternal !== true)
    useEffect(() => {
        if (!selectedTicket?.id) {
            setComments([]);
            return;
        }

        const commentsQ = query(
            collection(db, `${paths.tickets}/${selectedTicket.id}/comments`),
            orderBy('createdAt', 'asc')
        );

        const unsubscribe = onSnapshot(commentsQ, (snapshot) => {
            const list = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(c => c.isInternal !== true); // SEGURANÇA: oculta discussões internas dos técnicos!
            setComments(list);
        });

        return () => unsubscribe();
    }, [selectedTicket?.id, paths.tickets]);

    // 4. Carregar Ativos da Empresa e Inventário (Somente User Company com módulo liberado)
    useEffect(() => {
        if (portalUser?.role !== 'User Company' || !portalUser?.companyId) return;
        if (portalUser?.modules && portalUser.modules.assets === false) {
            setAssets([]);
            setEquipmentList([]);
            setLoadingAssets(false);
            setLoadingEquipment(false);
            return;
        }

        // Buscar clientes no dbAsset para pegar os devices sincronizados pelo agente
        const aamQ = query(collection(dbAsset, "customers"), where("intranet_id", "==", portalUser.companyId));
        let unsubDevices = () => {};

        const unsubAam = onSnapshot(aamQ, (snapshot) => {
            if (!snapshot.empty) {
                const customerDoc = snapshot.docs[0];
                unsubDevices = onSnapshot(collection(dbAsset, "customers", customerDoc.id, "devices"), (devSnap) => {
                    const devList = devSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                    setAssets(devList);
                    setLoadingAssets(false);
                });
            } else {
                setAssets([]);
                setLoadingAssets(false);
            }
        });

        // Buscar outros equipamentos cadastrados no cliente
        const compColl = portalUser.companyType || 'clients';
        const equipColl = collection(db, `/artifacts/${appId}/public/data/${compColl}/${portalUser.companyId}/equipment`);
        const unsubEquip = onSnapshot(equipColl, (snap) => {
            setEquipmentList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoadingEquipment(false);
        }, (err) => {
            console.error("Erro ao carregar equipamentos:", err);
            setLoadingEquipment(false);
        });

        // Escutar status RTDB
        const statusRef = rtdbRef(rtdb, 'status');
        const unsubRtdb = onValue(statusRef, (snap) => {
            const data = snap.val();
            if (data) setLiveData(data);
        });

        return () => {
            unsubAam();
            unsubDevices();
            unsubEquip();
            unsubRtdb();
        };
    }, [portalUser]);

    // 5. Carregar Contratos da Empresa (Para visualização do User Company e para abertura de chamados)
    useEffect(() => {
        if (!portalUser?.companyId) return;

        // Se for User Company e tiver explicitamente módulo de contratos desativado, oculta aba de contratos
        const contractsModuleDisabled = portalUser.role === 'User Company' && portalUser?.modules && portalUser.modules.contracts === false;
        if (contractsModuleDisabled) {
            setContracts([]);
            setLoadingContracts(false);
            return;
        }

        setLoadingContracts(true);
        const qContracts = query(collection(db, paths.contracts), orderBy("clientName", "asc"));
        const unsubscribe = onSnapshot(qContracts, (snapshot) => {
            const allContracts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            const companyNameNorm = (portalUser.companyName || '').toLowerCase().trim();
            const fantasyNorm = (companyData?.nomeFantasia || '').toLowerCase().trim();
            const nameNorm = (companyData?.name || '').toLowerCase().trim();

            const myContracts = allContracts.filter(c => {
                const cId = String(c.clientId || '');
                const cName = (c.clientName || '').toLowerCase().trim();
                return cId === String(portalUser.companyId) ||
                    (companyNameNorm && cName === companyNameNorm) ||
                    (fantasyNorm && cName === fantasyNorm) ||
                    (nameNorm && cName === nameNorm);
            });

            const enrichedContracts = myContracts.map(c => {
                const matchedTemplate = contractTemplates.find(t => t.id === c.templateId);
                const resolvedName = c.contractName || c.templateName || matchedTemplate?.name || c.name || 'Contrato de Suporte';
                return {
                    ...c,
                    contractName: resolvedName
                };
            });

            setContracts(enrichedContracts);
            setLoadingContracts(false);
            if (enrichedContracts.length > 0) {
                if (!selectedContract) setSelectedContract(enrichedContracts[0]);
                // Se ainda estiver com o padrão inicial de ticketContractId, seleciona o primeiro contrato ativo
                setTicketContractId(prev => (prev === 'outros' || !prev) ? enrichedContracts[0].id : prev);
                setTicketContractName(prev => (prev === 'Outros' || !prev) ? enrichedContracts[0].contractName : prev);
            }
        }, (err) => {
            console.error("Erro ao carregar contratos:", err);
            setLoadingContracts(false);
        });

        return () => unsubscribe();
    }, [portalUser, companyData, paths.contracts, selectedContract, contractTemplates]);

    // 6. Carregar Versões do Contrato Selecionado
    useEffect(() => {
        if (!selectedContract?.id) {
            setContractVersions([]);
            setSelectedVersion(null);
            return;
        }

        setLoadingVersions(true);
        const versionsPath = `${paths.contracts}/${selectedContract.id}/versions`;
        const qVersions = query(collection(db, versionsPath), orderBy("versionNumber", "desc"));
        const unsubscribe = onSnapshot(qVersions, (snapshot) => {
            const vList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setContractVersions(vList);
            if (vList.length > 0) {
                setSelectedVersion(vList[0]);
            } else {
                setSelectedVersion({
                    versionNumber: selectedContract.currentVersion || 1,
                    content: selectedContract.content,
                    savedAt: selectedContract.updatedAt || selectedContract.createdAt,
                    notes: 'Versão atual'
                });
            }
            setLoadingVersions(false);
        }, (err) => {
            console.error("Erro ao carregar versões do contrato:", err);
            setLoadingVersions(false);
        });

        return () => unsubscribe();
    }, [selectedContract, paths.contracts]);

    // 7. Carregar Laudos Técnicos da Empresa (Somente User Company com módulo liberado)
    useEffect(() => {
        if (portalUser?.role !== 'User Company' || !portalUser?.companyId) return;
        if (portalUser?.modules && portalUser.modules.reports === false) {
            setReports([]);
            setLoadingReports(false);
            return;
        }

        setLoadingReports(true);
        const qReports = query(collection(db, paths.reports), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(qReports, (snapshot) => {
            const allReports = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            const companyNameNorm = (portalUser.companyName || '').toLowerCase().trim();
            const fantasyNorm = (companyData?.nomeFantasia || '').toLowerCase().trim();
            const nameNorm = (companyData?.name || '').toLowerCase().trim();

            const myReports = allReports.filter(r => {
                const rName = (r.clientName || '').toLowerCase().trim();
                return (companyNameNorm && rName === companyNameNorm) ||
                    (fantasyNorm && rName === fantasyNorm) ||
                    (nameNorm && rName === nameNorm);
            });

            setReports(myReports);
            setLoadingReports(false);
        }, (err) => {
            console.error("Erro ao carregar laudos técnicos:", err);
            setLoadingReports(false);
        });

        return () => unsubscribe();
    }, [portalUser, companyData, paths.reports]);

    // Helpers
    const isFinished = (statusName) => {
        const s = statusName?.toLowerCase() || '';
        return s.includes('finalizado') || s.includes('concluido') || s.includes('concluído') || s.includes('fechado') || s.includes('faturado');
    };

    const filteredTickets = useMemo(() => {
        let list = tickets;
        if (ticketFilter === 'open') {
            list = list.filter(t => !isFinished(t.status));
        } else if (ticketFilter === 'closed') {
            list = list.filter(t => isFinished(t.status));
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(t => 
                (t.title && t.title.toLowerCase().includes(term)) ||
                (t.numeroSequencial && String(t.numeroSequencial).includes(term)) ||
                (t.numeroSequencial && (`a${t.numeroSequencial}`).includes(term)) ||
                (t.clientName && t.clientName.toLowerCase().includes(term))
            );
        }
        return list;
    }, [tickets, ticketFilter, searchTerm]);

    const stats = useMemo(() => {
        const total = tickets.length;
        const open = tickets.filter(t => !isFinished(t.status)).length;
        const closed = tickets.filter(t => isFinished(t.status)).length;
        return { total, open, closed };
    }, [tickets]);

    // Estatísticas Detalhadas para o Dashboard (Restrito ao escopo da empresa / usuário logado)
    const dashboardStats = useMemo(() => {
        const total = tickets.length;
        const active = tickets.filter(t => !isFinished(t.status)).length;
        const finished = tickets.filter(t => isFinished(t.status)).length;
        const scheduled = tickets.filter(t => t.isScheduled).length;

        // Chamados por Status
        const statusCounts = {};
        tickets.forEach(t => {
            const s = t.status || 'Sem Status';
            statusCounts[s] = (statusCounts[s] || 0) + 1;
        });

        // Chamados por Técnico Responsável
        const techCounts = {};
        tickets.forEach(t => {
            if (t.assignedToEmail && t.assignedToEmail.length > 0) {
                t.assignedToEmail.forEach(email => {
                    const name = email.split('@')[0];
                    techCounts[name] = (techCounts[name] || 0) + 1;
                });
            } else {
                techCounts['Sem atribuição'] = (techCounts['Sem atribuição'] || 0) + 1;
            }
        });

        // Top Solicitantes da Empresa (Usuários que mais abriram chamados)
        const requesterCounts = {};
        tickets.forEach(t => {
            const requester = t.clientName || (t.createdByEmail ? t.createdByEmail.split('@')[0] : 'Não identificado');
            requesterCounts[requester] = (requesterCounts[requester] || 0) + 1;
        });

        return {
            total, active, finished, scheduled,
            statusCounts, techCounts, requesterCounts
        };
    }, [tickets]);

    // Agrupamento de Equipamentos do Inventário por Categoria (Idêntico à Intranet Arcus)
    const groupedEquipment = useMemo(() => {
        const grouped = {};
        equipmentList.forEach(item => {
            const type = item.type || 'Outros';
            
            // FILTRO DE SEGURANÇA GRANULAR - ABSOLUTO (FAIL-CLOSED):
            // O GESTOR (User Company) SÓ PODE VER se estiver explicitamente liberado na sua array de permissões.
            if (portalUser?.role === 'User Company') {
                const allowedTypes = portalUser?.modules?.inventoryTypes || [];
                if (!allowedTypes.includes(type)) {
                    return; // Bloqueia a visualização deste equipamento de forma absoluta
                }
            }

            if (!grouped[type]) grouped[type] = [];
            grouped[type].push(item);
        });
        return grouped;
    }, [equipmentList, portalUser]);

    const getFilteredEquipmentItems = (type) => {
        const items = groupedEquipment[type] || [];
        const query = (equipmentSearchQueries[type] || '').toLowerCase().trim();
        if (!query) return items;
        return items.filter(item => {
            const nome = (item.data?.nome || '').toLowerCase();
            const ip = (item.data?.ip || '').toLowerCase();
            const modelo = (item.data?.modelo || '').toLowerCase();
            return nome.includes(query) || ip.includes(query) || modelo.includes(query);
        });
    };

    // Abertura de Novo Chamado
    const handleCreateTicket = async (e) => {
        e.preventDefault();
        setTicketFeedback({ type: '', msg: '' });

        if (!ticketTitle.trim() || !ticketDescription.trim()) {
            setTicketFeedback({ type: 'error', msg: 'Por favor, preencha o assunto e a descrição do problema.' });
            return;
        }

        setSubmittingTicket(true);
        try {
            const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
            const uploadedAttachments = [];

            // Upload de anexos se houver
            for (const file of selectedFiles) {
                const storageRef = ref(storage, `tickets/portal_${Date.now()}_${file.name}`);
                const res = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(res.ref);
                uploadedAttachments.push({
                    name: file.name,
                    url,
                    type: file.type,
                    path: storageRef.fullPath
                });
            }

            const ticketDocRef = doc(collection(db, paths.tickets));

            await runTransaction(db, async (transaction) => {
                const counterDoc = await transaction.get(doc(db, paths.counter));
                const currentSeq = (counterDoc.data()?.currentId || 0) + 1;

                transaction.set(ticketDocRef, {
                    title: ticketTitle.trim(),
                    description: ticketDescription.trim(),
                    priority: ticketPriority,
                    contractId: ticketContractId === 'outros' ? null : ticketContractId,
                    contractName: ticketContractName || 'Outros',
                    clientCompanyId: portalUser.companyId,
                    clientCompany: portalUser.companyName,
                    clientCompanyName: companyData?.name || portalUser.companyName,
                    clientName: portalUser.name,
                    clientEmail: portalUser.email,
                    clientWhatsApp: portalUser.phone || '',
                    clientUserId: portalUser.id,
                    createdByEmail: portalUser.email,
                    source: 'portal_web',
                    status: 'Novo Chamado',
                    numeroSequencial: currentSeq,
                    attachments: uploadedAttachments,
                    assignedTo: [],
                    assignedToEmail: [],
                    createdAt: serverTimestamp()
                });

                transaction.update(doc(db, paths.counter), { currentId: currentSeq });
            });

            setTicketFeedback({ type: 'success', msg: 'Chamado aberto com sucesso! Nossa equipe técnica já foi notificada.' });
            setTimeout(() => {
                setIsNewTicketOpen(false);
                setTicketTitle('');
                setTicketDescription('');
                setTicketPriority('low');
                setTicketContractId('outros');
                setTicketContractName('Outros');
                setSelectedFiles([]);
                setTicketFeedback({ type: '', msg: '' });
            }, 1500);
        } catch (error) {
            console.error("Erro ao abrir chamado pelo portal:", error);
            setTicketFeedback({ type: 'error', msg: 'Erro ao abrir chamado: ' + error.message });
        } finally {
            setSubmittingTicket(false);
        }
    };

    // Enviar comentário no chamado
    const handleSendComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() && commentFiles.length === 0) return;
        if (!selectedTicket?.id) return;

        setSendingComment(true);
        try {
            let fileAttachmentText = '';
            if (commentFiles.length > 0) {
                const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
                const fileLinks = [];
                for (const file of commentFiles) {
                    const storageRef = ref(storage, `tickets/${selectedTicket.id}/portal_comment_${Date.now()}_${file.name}`);
                    const uploadResult = await uploadBytes(storageRef, file);
                    const downloadUrl = await getDownloadURL(uploadResult.ref);
                    fileLinks.push(`[${file.name}](${downloadUrl})`);
                }
                fileAttachmentText = `\n\n📎 **Anexos:**\n` + fileLinks.join('\n');
            }

            const fullText = (newComment.trim() + fileAttachmentText).trim();

            await addDoc(collection(db, `${paths.tickets}/${selectedTicket.id}/comments`), {
                text: fullText,
                userName: portalUser.name,
                userEmail: portalUser.email,
                isPortalUser: true,
                isInternal: false, // Público para técnicos e clientes
                createdAt: serverTimestamp()
            });

            // Atualiza a flag do chamado para indicar resposta do cliente
            await updateDoc(doc(db, paths.tickets, selectedTicket.id), {
                clientReplied: true
            });

            setNewComment('');
            setCommentFiles([]);
        } catch (error) {
            console.error("Erro ao enviar mensagem:", error);
            alert("Erro ao enviar mensagem: " + error.message);
        } finally {
            setSendingComment(false);
        }
    };

    // Assinatura Eletrônica do Contrato pelo Cliente no Portal
    const handleSignContractClient = async (sigData) => {
        if (!selectedContract) return;

        setIsSubmittingSignature(true);
        try {
            // Obter IP público do cliente para auditoria jurídica
            let publicIp = 'IP não identificado';
            try {
                const ipRes = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(3000) });
                if (ipRes.ok) {
                    const ipJson = await ipRes.json();
                    publicIp = ipJson.ip || publicIp;
                }
            } catch (e) {
                console.warn("Não foi possível obter IP público do cliente:", e);
            }

            const fullSignatureObj = {
                signatureBase64: sigData.signatureBase64,
                signerName: sigData.signerName,
                signerDocument: sigData.signerDocument,
                signerEmail: portalUser.email,
                signedAt: new Date().toISOString(),
                signerRole: 'client',
                ipAddress: publicIp,
                userAgent: navigator.userAgent
            };

            const updatedSignatures = {
                ...(selectedContract.signatures || {}),
                client: fullSignatureObj
            };

            // Atualizar no documento principal do contrato no Firestore
            const contractDocRef = doc(db, paths.contracts, selectedContract.id);
            await updateDoc(contractDocRef, {
                signatures: updatedSignatures,
                updatedAt: serverTimestamp()
            });

            // Atualizar na versão se aplicável
            if (selectedVersion?.id) {
                const verDocRef = doc(db, `${paths.contracts}/${selectedContract.id}/versions`, selectedVersion.id);
                await updateDoc(verDocRef, {
                    signatures: updatedSignatures
                }).catch(() => {});
            }

            // Atualizar estado em memória
            setSelectedContract(prev => prev ? {
                ...prev,
                signatures: updatedSignatures
            } : prev);

            setSelectedVersion(prev => prev ? {
                ...prev,
                signatures: updatedSignatures
            } : prev);

            setIsSignContractModalOpen(false);
            alert("✓ Contrato assinado eletronicamente com sucesso! O documento foi oficializado.");
        } catch (error) {
            console.error("Erro ao assinar contrato pelo cliente:", error);
            alert("Erro ao gravar assinatura: " + error.message);
        } finally {
            setIsSubmittingSignature(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-slate-950 text-white font-sans selection:bg-cyan-500 selection:text-white">
            
            <ClientPortalSidebar 
                isOpen={isSidebarOpen} 
                onClose={() => setIsSidebarOpen(false)} 
                onOpen={() => setIsSidebarOpen(true)}
                activeTab={activeTab} 
                setActiveTab={setActiveTab} 
                portalUser={portalUser} 
                stats={stats} 
                contracts={contracts} 
                reports={reports} 
                assets={assets} 
                equipmentList={equipmentList} 
                onLogout={onLogout}
            />

            {/* Conteúdo Principal */}
            <main className="flex-1 p-4 sm:p-8 pt-20 lg:pt-8 max-w-7xl w-full mx-auto space-y-6 overflow-y-auto h-screen custom-scrollbar">
                
                {/* ABA 1: CHAMADOS */}
                {activeTab === 'tickets' && (
                    <div className="space-y-6">
                        {/* Barra Superior de Ações e Filtros */}
                        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 bg-slate-900/50 p-4 rounded-2xl border border-slate-800">
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={() => {
                                        setTicketFeedback({ type: '', msg: '' });
                                        setIsNewTicketOpen(true);
                                    }}
                                    className="px-5 py-2.5 bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white font-black rounded-xl transition-all shadow-lg shadow-cyan-900/30 flex items-center gap-2 text-xs uppercase tracking-wider active:scale-95"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Abrir Novo Chamado
                                </button>
                                <div className="flex bg-slate-800 rounded-xl p-1 border border-slate-700">
                                    <button
                                        onClick={() => setTicketFilter('open')}
                                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                                            ticketFilter === 'open' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-white'
                                        }`}
                                    >
                                        Em Aberto ({stats.open})
                                    </button>
                                    <button
                                        onClick={() => setTicketFilter('closed')}
                                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                                            ticketFilter === 'closed' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-white'
                                        }`}
                                    >
                                        Concluídos ({stats.closed})
                                    </button>
                                    <button
                                        onClick={() => setTicketFilter('all')}
                                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                                            ticketFilter === 'all' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-white'
                                        }`}
                                    >
                                        Todos ({stats.total})
                                    </button>
                                </div>

                                <button
                                    onClick={() => setIsDashboardOpen(true)}
                                    className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-cyan-400 hover:text-cyan-300 font-bold text-xs uppercase tracking-wider rounded-xl border border-cyan-500/30 hover:border-cyan-500/60 shadow-lg shadow-cyan-950/40 transition-all flex items-center gap-2 active:scale-95"
                                    title="Abrir Dashboard de Estatísticas"
                                >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    Dashboard
                                </button>
                            </div>

                            <div className="relative flex-1 sm:max-w-xs">
                                <input
                                    type="text"
                                    placeholder="Buscar chamado ou nº..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl py-2 px-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500"
                                />
                            </div>
                        </div>

                        {/* Listagem de Chamados */}
                        {loadingTickets ? (
                            <div className="py-20 text-center text-slate-400 animate-pulse font-medium">
                                Carregando chamados...
                            </div>
                        ) : filteredTickets.length === 0 ? (
                            <div className="py-20 text-center bg-slate-900 border border-dashed border-slate-800 rounded-3xl p-8">
                                <div className="text-4xl mb-3">🎫</div>
                                <h4 className="text-lg font-bold text-white mb-1">
                                    {searchTerm 
                                        ? 'Nenhum resultado encontrado' 
                                        : portalUser.role === 'User Company' 
                                            ? 'Nenhum chamado registrado para a sua empresa' 
                                            : 'Nenhum chamado aberto por você ainda'}
                                </h4>
                                <p className="text-sm text-slate-400 max-w-md mx-auto mb-4">
                                    {searchTerm 
                                        ? 'Tente pesquisar por outro número ou palavra-chave.' 
                                        : portalUser.role === 'User Company'
                                            ? 'Sua empresa ainda não possui chamados nesta visualização. Precisa de suporte técnico?'
                                            : 'Como seu perfil de acesso é "User (Individual)", você visualiza apenas os chamados abertos por você mesmo.'}
                                </p>
                                {portalUser.role === 'User' && !searchTerm && (
                                    <p className="text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 max-w-md mx-auto mb-6">
                                        💡 <strong>Dica:</strong> Se você precisa visualizar todos os chamados abertos por qualquer colaborador da sua empresa ({portalUser.companyName}), solicite à equipe Arcus a alteração do seu perfil para <strong>User Company (Gestor)</strong>.
                                    </p>
                                )}
                                <button
                                    onClick={() => setIsNewTicketOpen(true)}
                                    className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-xl text-xs uppercase tracking-wider"
                                >
                                    Abrir Chamado Agora
                                </button>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-3xl border border-slate-800 bg-slate-900/60 shadow-xl">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-900 border-b border-slate-800 text-[11px] font-black uppercase tracking-wider text-slate-400">
                                            <th className="p-5 w-24">Nº</th>
                                            <th className="p-5">Assunto / Descrição</th>
                                            {portalUser.role === 'User Company' && <th className="p-5 w-40">Solicitante</th>}
                                            <th className="p-5 w-32">Data</th>
                                            <th className="p-5 w-40">Status</th>
                                            <th className="p-5 w-40 whitespace-nowrap">Técnico</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800/60 text-sm">
                                        {filteredTickets.map(t => {
                                            const finished = isFinished(t.status);
                                            const dateStr = t.createdAt?.toDate ? t.createdAt.toDate().toLocaleDateString('pt-BR') : '—';
                                            const assignedTechs = t.assignedToEmail && t.assignedToEmail.length > 0
                                                ? t.assignedToEmail.map(e => e.split('@')[0]).join(', ')
                                                : null;
                                            return (
                                                <tr 
                                                    key={t.id}
                                                    onClick={() => setSelectedTicket(t)}
                                                    className="hover:bg-slate-800/40 transition-colors cursor-pointer group"
                                                >
                                                    <td className="p-5 font-mono font-bold text-cyan-400">
                                                        A{t.numeroSequencial || '—'}
                                                    </td>
                                                    <td className="p-5">
                                                        <span className="font-bold text-white group-hover:text-cyan-300 transition-colors block text-base">
                                                            {t.title}
                                                        </span>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            {t.contractName && (
                                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                                                    t.contractName === 'Outros'
                                                                        ? 'bg-amber-950/40 text-amber-400 border-amber-800/40'
                                                                        : 'bg-emerald-950/40 text-emerald-400 border-emerald-800/40'
                                                                }`}>
                                                                    {t.contractName}
                                                                </span>
                                                            )}
                                                            <span className="text-xs text-slate-400 line-clamp-1">
                                                                {t.description}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    {portalUser.role === 'User Company' && (
                                                        <td className="p-5 text-xs text-slate-300 font-medium">
                                                            <div className="truncate max-w-[150px]">
                                                                {t.clientName || t.createdByEmail || '—'}
                                                            </div>
                                                        </td>
                                                    )}
                                                    <td className="p-5 text-xs text-slate-400 font-mono">
                                                        {dateStr}
                                                    </td>
                                                    <td className="p-5">
                                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                                                            finished
                                                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                                                : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                                                        }`}>
                                                            <span className={`w-1.5 h-1.5 rounded-full ${finished ? 'bg-emerald-400' : 'bg-cyan-400 animate-pulse'}`}></span>
                                                            {t.status || 'Novo Chamado'}
                                                        </span>
                                                    </td>
                                                    <td className="p-5 text-xs whitespace-nowrap">
                                                        {assignedTechs ? (
                                                            <span className="text-cyan-300 font-medium bg-cyan-950/40 px-2.5 py-1 rounded-md border border-cyan-800/40 inline-block max-w-[120px] truncate align-middle">
                                                                👤 {assignedTechs}
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-500 italic whitespace-nowrap">
                                                                Sem atribuição
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ABA 2: DADOS DA EMPRESA */}
                {activeTab === 'company' && (
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 space-y-8">
                        <div className="border-b border-slate-800 pb-5 flex items-center justify-between">
                            <div>
                                <h3 className="text-2xl font-black text-white">Dados Cadastrais da Empresa</h3>
                                <p className="text-xs text-slate-400 mt-1">Informações registradas na base técnica da Arcus Informática</p>
                            </div>
                            <div className="flex items-center gap-3">
                                {portalUser.role === 'User Company' && (
                                    <button 
                                        onClick={() => setIsUsersModalOpen(true)}
                                        className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold py-2 px-4 rounded-xl transition-all border border-slate-700 flex items-center gap-2 text-xs shadow-xl active:scale-95"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                        Gerenciar Acessos Web
                                    </button>
                                )}
                                <span className="text-xs font-bold px-3 py-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-full hidden sm:block">
                                    Modo Leitura
                                </span>
                            </div>
                        </div>

                        {loadingCompany ? (
                            <div className="py-12 text-center text-slate-400 animate-pulse">Carregando dados da empresa...</div>
                        ) : companyData ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-800 space-y-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Razão Social / Nome</span>
                                    <span className="text-lg font-bold text-white block">{companyData.name}</span>
                                </div>

                                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-800 space-y-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Nome Fantasia</span>
                                    <span className="text-lg font-bold text-white block">{companyData.nomeFantasia || companyData.name}</span>
                                </div>

                                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-800 space-y-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">CNPJ / CPF</span>
                                    <span className="text-base font-mono font-bold text-cyan-400 block">{companyData.cnpj || '—'}</span>
                                </div>

                                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-800 space-y-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Telefone Principal</span>
                                    <span className="text-base font-medium text-slate-200 block">{companyData.telefone || '—'}</span>
                                </div>

                                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-800 space-y-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">E-mail Corporativo</span>
                                    <span className="text-base font-medium text-slate-200 block">{companyData.email || '—'}</span>
                                </div>

                                <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-800 space-y-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Endereço Cadastrado</span>
                                    <span className="text-base font-medium text-slate-200 block">{companyData.endereco || '—'}</span>
                                </div>
                            </div>
                        ) : (
                            <p className="text-slate-400">Não foi possível carregar os dados da empresa.</p>
                        )}
                    </div>
                )}

                {/* ABA 3: GESTÃO DO CONTRATO (APENAS USER COMPANY) */}
                {activeTab === 'contracts' && portalUser.role === 'User Company' && (
                    <div className="space-y-6">
                        <div className="bg-slate-900 border border-slate-800 p-6 sm:p-8 rounded-3xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h3 className="text-2xl font-black text-white">Gestão de Contratos</h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    Consulte os termos contratuais e histórico de versões formalizados com a Arcus Tecnologia.
                                </p>
                            </div>
                            <span className="text-xs font-bold px-3 py-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-full">
                                Documento Oficial • Apenas Consulta
                            </span>
                        </div>

                        {loadingContracts ? (
                            <div className="py-20 text-center text-slate-400 animate-pulse">Carregando contratos...</div>
                        ) : contracts.length === 0 ? (
                            <div className="py-16 text-center bg-slate-900 border border-dashed border-slate-800 rounded-3xl p-8">
                                <div className="text-4xl mb-3">📄</div>
                                <h4 className="text-base font-bold text-white mb-1">Nenhum contrato formalizado</h4>
                                <p className="text-xs text-slate-400 max-w-md mx-auto">
                                    Não foram encontrados contratos ativos registrados para sua empresa no momento.
                                </p>
                            </div>
                        ) : (
                        <div className="space-y-6">
                            {/* Grid de Contratos em Miniatura A4 */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-400">Contratos Disponíveis</h4>
                                    <span className="text-[11px] font-mono text-cyan-400 bg-cyan-950/70 border border-cyan-800/40 px-2 py-0.5 rounded">
                                        {contracts.length} {contracts.length === 1 ? 'Contrato' : 'Contratos'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                                    {contracts.map(c => {
                                        const isSel = selectedContract?.id === c.id;
                                        const contractClauses = c.clauses || [];
                                        const firstClause = contractClauses.length > 0 ? contractClauses[0] : null;
                                        const firstGroup = firstClause?.group || (contractClauses.length > 0 ? 'DO OBJETO DO CONTRATO' : '');

                                        return (
                                            <div
                                                key={c.id}
                                                onClick={() => {
                                                    setSelectedContract(c);
                                                    setIsViewContractModalOpen(true);
                                                }}
                                                className={`group/card bg-slate-900 border rounded-2xl p-3 flex flex-col justify-between transition-all duration-300 shadow-lg cursor-pointer ${
                                                    isSel
                                                        ? 'border-cyan-500 shadow-[0_8px_30px_-8px_rgba(6,182,212,0.4)] ring-2 ring-cyan-500/30'
                                                        : 'border-slate-800 hover:border-cyan-500/60 hover:shadow-[0_8px_30px_-8px_rgba(6,182,212,0.25)]'
                                                }`}
                                            >
                                                {/* Top Bar da Miniatura */}
                                                <div className="flex items-center justify-between mb-2 px-0.5">
                                                    <div className="flex items-center gap-1.5 min-w-0 pr-1">
                                                        <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0"></span>
                                                        <h3 className="text-[11px] font-black text-white uppercase tracking-tight truncate" title={c.clientName}>
                                                            {c.clientName}
                                                        </h3>
                                                    </div>
                                                    <span className="text-[9px] font-mono text-cyan-400 bg-cyan-950/70 border border-cyan-800/40 px-1.5 py-0.2 rounded shrink-0">
                                                        V{c.currentVersion || 1}
                                                    </span>
                                                </div>

                                                {/* Miniatura A4 Proporcional e Compacta (Página 1 / Capa do Contrato) */}
                                                <div 
                                                    className="relative w-full aspect-[1/1.38] bg-slate-950/70 rounded-xl p-2.5 flex items-center justify-center overflow-hidden border border-slate-800 group-hover/card:border-cyan-500/40 transition-colors"
                                                    title="Clique para visualizar o documento oficial e histórico de versões em modal"
                                                >
                                                    {/* Folha de Papel A4 Branca com Conteúdo Real */}
                                                    <div className="w-full h-full bg-white text-slate-900 rounded shadow-md p-3 flex flex-col justify-between overflow-hidden select-none border border-slate-250 relative group-hover/card:scale-[1.02] transition-transform duration-200">
                                                        
                                                        <div className="space-y-2">
                                                            {/* Cabeçalho Oficial: Logotipo + Título Centralizado */}
                                                            <div className="border-b border-slate-800/30 pb-1.5">
                                                                {c.logo ? (
                                                                    <div className="flex justify-start mb-1">
                                                                        <img 
                                                                            src={c.logo} 
                                                                            alt="Logo" 
                                                                            className="h-4 sm:h-5 object-contain"
                                                                            style={{ maxWidth: '85px' }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center gap-1 mb-1 text-slate-800">
                                                                        <div className="w-3 h-3 rounded bg-cyan-600 flex items-center justify-center text-[7px] text-white font-black">A</div>
                                                                        <span className="text-[7px] font-black tracking-wider uppercase text-slate-700">Arcus Tecnologia</span>
                                                                    </div>
                                                                )}
                                                                <h4 className="text-[7.5px] font-black text-slate-950 uppercase text-center leading-tight tracking-tight line-clamp-2">
                                                                    {c.contractName || c.templateName || 'Contrato de Prestação de Serviços'}
                                                                </h4>
                                                            </div>

                                                            {/* Qualificação Real das Partes */}
                                                            <div className="text-[6.5px] text-slate-600 leading-tight space-y-0.5 text-justify">
                                                                <p className="line-clamp-2">
                                                                    <strong>CONTRATANTE:</strong> {c.clientName || 'Cliente'}.
                                                                </p>
                                                                <p className="line-clamp-2">
                                                                    <strong>CONTRATADA:</strong> ARCUS IT NETWORK SOLUTIONS TI LTDA - ME.
                                                                </p>
                                                            </div>

                                                            {/* Cláusula(s) Reais do Contrato */}
                                                            {firstClause ? (
                                                                <div className="border-t border-slate-200/80 pt-1.5 space-y-1">
                                                                    {firstGroup && (
                                                                        <div className="bg-slate-100 rounded px-1 py-0.2 text-[6px] font-black uppercase text-slate-700 tracking-wider line-clamp-1">
                                                                            📁 {firstGroup}
                                                                        </div>
                                                                    )}
                                                                    <p className="text-[6.5px] text-slate-700 line-clamp-4 leading-relaxed text-justify">
                                                                        <strong className="text-slate-900 font-black">
                                                                            {firstClause.title && !/^cl[áa]usula\s*\d+[ªº°]?/i.test(firstClause.title.trim()) 
                                                                                ? `Cláusula 1ª - ${firstClause.title}: ` 
                                                                                : 'Cláusula 1ª: '}
                                                                        </strong>
                                                                        {firstClause.text}
                                                                    </p>
                                                                </div>
                                                            ) : c.content ? (
                                                                <div className="border-t border-slate-200/80 pt-1">
                                                                    <div 
                                                                        className="text-[6.5px] text-slate-600 line-clamp-5 leading-tight"
                                                                        dangerouslySetInnerHTML={{ 
                                                                            __html: c.content.replace(/<h1[^>]*>.*?<\/h1>/gi, '').replace(/<img[^>]*>/gi, '') 
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : null}
                                                        </div>

                                                        {/* Rodapé da Folha A4 */}
                                                        <div className="pt-1 border-t border-slate-200 flex justify-between items-center text-[6px] text-slate-400 font-mono">
                                                            <span>Doc. Oficial Arcus</span>
                                                            <span>Pág. 1</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Status de Assinatura Eletrônica no Card */}
                                                <div className="mt-2 mb-0.5 flex items-center justify-between">
                                                    {(() => {
                                                        const isArcusSigned = !!c.signatures?.arcus?.signedAt;
                                                        const isClientSigned = !!c.signatures?.client?.signedAt;
                                                        if (isArcusSigned && isClientSigned) {
                                                            return (
                                                                <span className="w-full inline-flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-wider text-emerald-400 bg-emerald-950/70 border border-emerald-800/60 py-1 px-2 rounded-lg shadow-sm">
                                                                    <span>✓</span> Vigente & Assinado
                                                                </span>
                                                            );
                                                        }
                                                        if (!isClientSigned) {
                                                            return (
                                                                <span className="w-full inline-flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-wider text-amber-300 bg-amber-950/70 border border-amber-800/60 py-1 px-2 rounded-lg shadow-sm animate-pulse">
                                                                    <span>✍️</span> Assinatura Pendente
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <span className="w-full inline-flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-wider text-cyan-300 bg-cyan-950/70 border border-cyan-800/60 py-1 px-2 rounded-lg shadow-sm">
                                                                <span>⏳</span> Em Validação Arcus
                                                            </span>
                                                        );
                                                    })()}
                                                </div>

                                                {/* Barra Inferior com Identificador */}
                                                <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-800 text-[10px] text-slate-400">
                                                    <span className="truncate text-cyan-400/90 font-medium" title={c.contractName || c.templateName}>
                                                        {c.contractName || c.templateName || 'Contrato TI'}
                                                    </span>
                                                    <span className="text-[9px] text-slate-500 font-mono">
                                                        {c.updatedAt?.toDate ? c.updatedAt.toDate().toLocaleDateString('pt-BR') : '—'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* MODAL DE VISUALIZAÇÃO DO CONTRATO & HISTÓRICO DE VERSÕES */}
                            {isViewContractModalOpen && selectedContract && (
                                <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex justify-center items-center z-[150] p-4 animate-fadeIn">
                                    <div className="bg-slate-900 border border-slate-700/80 rounded-3xl p-6 sm:p-8 shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto custom-scrollbar flex flex-col animate-scaleIn space-y-6">
                                        {/* Top Bar de Detalhes com Título e Ação de Impressão */}
                                        <div className="flex justify-between items-start flex-wrap gap-4 pb-5 border-b border-slate-800">
                                            <div>
                                                <div className="flex items-center gap-3 mb-1 flex-wrap">
                                                    <h3 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">{selectedContract.clientName}</h3>
                                                    <span className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider">
                                                        V{selectedContract.currentVersion || 1} Ativa
                                                    </span>
                                                    <span className="bg-slate-950 text-slate-300 border border-slate-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
                                                        <span>{selectedContract.contractName || selectedContract.templateName || 'Contrato de Suporte'}</span>
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-400 mt-1">
                                                    Exibindo Versão {selectedVersion?.versionNumber || selectedContract?.currentVersion || 1} • Documento Oficial em Folha A4
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                {/* Botão de Assinatura Eletrônica do Cliente */}
                                                {(() => {
                                                    const isClientSigned = !!selectedContract.signatures?.client?.signedAt;
                                                    const clientSig = selectedContract.signatures?.client;

                                                    if (isClientSigned) {
                                                        return (
                                                            <div className="flex items-center gap-1.5 bg-emerald-950/70 border border-emerald-500/50 px-3.5 py-1.5 rounded-xl text-xs font-bold text-emerald-400 shadow-sm" title={`Assinado por ${clientSig.signerName} em ${new Date(clientSig.signedAt).toLocaleString('pt-BR')}`}>
                                                                <span>✓ Assinado por sua Empresa</span>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsSignContractModalOpen(true)}
                                                            className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-emerald-950/40 flex items-center gap-2 animate-pulse"
                                                            title="Assinar este contrato eletronicamente em nome da sua empresa"
                                                        >
                                                            <span>✍️ Assinar Contrato</span>
                                                        </button>
                                                    );
                                                })()}

                                                <button
                                                    onClick={() => window.print()}
                                                    className="px-4 py-2.5 bg-slate-800 hover:bg-slate-750 border border-slate-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md flex items-center gap-2"
                                                >
                                                    🖨️ Imprimir
                                                </button>
                                                <button 
                                                    onClick={() => setIsViewContractModalOpen(false)}
                                                    className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors"
                                                    title="Fechar Modal"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
                                            {/* Histórico de Versões */}
                                            <div className="lg:col-span-1 border-r border-slate-800/60 pr-0 lg:pr-6 space-y-3">
                                                <h4 className="text-xs font-black uppercase tracking-wider text-slate-400">Histórico de Versões</h4>
                                                {loadingVersions ? (
                                                    <p className="text-xs text-slate-500 italic">Carregando versões...</p>
                                                ) : contractVersions.length === 0 ? (
                                                    <p className="text-xs text-slate-500 italic">Nenhum histórico anterior registrado.</p>
                                                ) : (
                                                    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1 custom-scrollbar">
                                                        {contractVersions.map(v => {
                                                            const isVerSel = selectedVersion?.id === v.id || (selectedVersion?.versionNumber === v.versionNumber && !v.id);
                                                            const isCurrent = v.versionNumber === selectedContract.currentVersion;
                                                            return (
                                                                <div
                                                                    key={v.id || v.versionNumber}
                                                                    onClick={() => setSelectedVersion(v)}
                                                                    className={`p-3.5 rounded-xl border text-xs cursor-pointer transition-all ${
                                                                        isVerSel
                                                                            ? 'bg-cyan-950/60 border-cyan-500 text-white shadow-md'
                                                                            : 'bg-slate-950/60 border-slate-850 text-slate-400 hover:bg-slate-850 hover:text-slate-200'
                                                                    }`}
                                                                >
                                                                    <div className="flex justify-between items-center font-bold mb-1">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <span>Versão {v.versionNumber}</span>
                                                                            {isCurrent && (
                                                                                <span className="text-[9px] bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-black px-1.5 py-0.2 rounded-full uppercase">
                                                                                    Ativa
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span className="font-mono text-[10px] text-slate-500">
                                                                            {v.savedAt?.toDate ? v.savedAt.toDate().toLocaleDateString('pt-BR') : 'Atual'}
                                                                        </span>
                                                                    </div>
                                                                    {v.notes && <p className="text-[11px] text-slate-400 italic line-clamp-1">{v.notes}</p>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Visualizador de Folha A4 */}
                                            <div className="lg:col-span-2 space-y-4">
                                                <div className="flex justify-between items-center bg-slate-950/60 p-3.5 rounded-2xl border border-slate-800">
                                                    <span className="text-xs font-bold text-slate-300">
                                                        Visualizando Versão {selectedVersion?.versionNumber || selectedContract?.currentVersion || 1}
                                                    </span>
                                                    <span className="text-[10px] text-slate-500 font-mono bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                                                        Folha Oficial A4
                                                    </span>
                                                </div>

                                                {/* Status das Assinaturas Digitais do Contrato */}
                                                {(() => {
                                                    const sigs = selectedContract.signatures || {};
                                                    const arcus = sigs.arcus;
                                                    const client = sigs.client;
                                                    const isComplete = arcus?.signedAt && client?.signedAt;

                                                    return (
                                                        <div className={`p-4 rounded-2xl border transition-all ${isComplete ? 'bg-emerald-950/40 border-emerald-500/40' : 'bg-slate-950/70 border-slate-800'}`}>
                                                            <div className="flex justify-between items-center mb-3">
                                                                <span className="text-xs font-black uppercase tracking-wider text-slate-300">
                                                                    Assinaturas Digitais do Instrumento
                                                                </span>
                                                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase ${isComplete ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}`}>
                                                                    {isComplete ? '✓ Contrato Vigente (Assinado por Ambas as Partes)' : 'Assinatura Pendente'}
                                                                </span>
                                                            </div>

                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                                                {/* Arcus */}
                                                                <div className={`p-3 rounded-xl border ${arcus?.signedAt ? 'bg-emerald-950/50 border-emerald-800/60 text-emerald-200' : 'bg-slate-900 border-slate-800 text-slate-400'}`}>
                                                                    <div className="flex justify-between items-center mb-1">
                                                                        <span className="font-bold uppercase text-[10px] tracking-wider text-slate-400">Contratada (Arcus TI)</span>
                                                                        <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${arcus?.signedAt ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                                                            {arcus?.signedAt ? '✓ Assinado' : 'Pendente'}
                                                                        </span>
                                                                    </div>
                                                                    {arcus?.signedAt ? (
                                                                        <div className="space-y-0.5 text-[11px]">
                                                                            <p className="font-bold text-white">{arcus.signerName}</p>
                                                                            <p className="text-slate-500 font-mono text-[9px]">{new Date(arcus.signedAt).toLocaleString('pt-BR')}</p>
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-[11px] text-slate-500 italic pt-1">Aguardando assinatura da equipe Arcus</p>
                                                                    )}
                                                                </div>

                                                                {/* Cliente */}
                                                                <div className={`p-3 rounded-xl border ${client?.signedAt ? 'bg-emerald-950/50 border-emerald-800/60 text-emerald-200' : 'bg-slate-900 border-slate-800 text-slate-400'}`}>
                                                                    <div className="flex justify-between items-center mb-1">
                                                                        <span className="font-bold uppercase text-[10px] tracking-wider text-slate-400">Contratante (Sua Empresa)</span>
                                                                        <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${client?.signedAt ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                                                            {client?.signedAt ? '✓ Assinado' : 'Pendente'}
                                                                        </span>
                                                                    </div>
                                                                    {client?.signedAt ? (
                                                                        <div className="space-y-0.5 text-[11px]">
                                                                            <p className="font-bold text-white">{client.signerName}</p>
                                                                            <p className="text-slate-500 font-mono text-[9px]">{new Date(client.signedAt).toLocaleString('pt-BR')}</p>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex justify-between items-center pt-1">
                                                                            <span className="text-[11px] text-slate-500 italic">Pendente de sua assinatura</span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setIsSignContractModalOpen(true)}
                                                                                className="px-2.5 py-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg text-[10px] uppercase tracking-wider transition-colors shadow-sm"
                                                                            >
                                                                                Assinar Agora
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {/* Box de Anotações / Resumo da Alteração desta Versão no Portal do Cliente */}
                                                <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-4 rounded-2xl border border-cyan-500/30 shadow-lg flex items-start gap-3">
                                                    <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 shrink-0 mt-0.5">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-[11px] font-black uppercase tracking-wider text-cyan-400">
                                                                Anotações & Resumo da Alteração Contratual
                                                            </span>
                                                            <span className="text-[9px] font-mono text-slate-400 bg-slate-800 px-1.5 py-0.2 rounded border border-slate-700">
                                                                V{selectedVersion?.versionNumber || selectedContract?.currentVersion || 1}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-slate-200 leading-relaxed font-medium">
                                                            {selectedVersion?.notes?.trim() || selectedContract?.notes?.trim() || <span className="text-slate-500 italic">Nenhuma anotação informada para esta versão contratual.</span>}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="bg-slate-950 p-4 sm:p-8 rounded-2xl border border-slate-800/80 max-h-[580px] overflow-y-auto custom-scrollbar flex justify-center items-start">
                                                    <div className="w-full max-w-3xl bg-white text-slate-900 p-8 sm:p-12 rounded-xl shadow-2xl border border-slate-200 text-xs leading-relaxed print:p-0 print:shadow-none print:border-none">
                                                        <div 
                                                            dangerouslySetInnerHTML={{ 
                                                                __html: selectedVersion?.content || selectedContract?.content || '<p class="text-slate-500 italic">Sem conteúdo contratual disponível.</p>' 
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                )}

                {/* ABA 4: LAUDOS TÉCNICOS (APENAS USER COMPANY) */}
                {activeTab === 'reports' && portalUser.role === 'User Company' && (
                    <div className="space-y-6">
                        <div className="bg-slate-900 border border-slate-800 p-6 sm:p-8 rounded-3xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h3 className="text-2xl font-black text-white">Laudos Técnicos da Empresa</h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    Pareceres técnicos, diagnósticos, análises periciais e orçamentos emitidos pela Arcus.
                                </p>
                            </div>
                            <span className="text-xs font-bold px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full">
                                Documentos Periciais • Apenas Consulta
                            </span>
                        </div>

                        {/* Barra de Busca de Laudos */}
                        <div className="flex gap-4">
                            <input
                                type="text"
                                placeholder="Pesquisar laudo por número de OS, título, patrimônio..."
                                value={reportSearchTerm}
                                onChange={e => setReportSearchTerm(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-800 rounded-2xl px-5 py-3.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors shadow-inner"
                            />
                        </div>

                        {loadingReports ? (
                            <div className="py-20 text-center text-slate-400 animate-pulse">Carregando laudos técnicos...</div>
                        ) : reports.length === 0 ? (
                            <div className="py-16 text-center bg-slate-900 border border-dashed border-slate-800 rounded-3xl p-8">
                                <div className="text-4xl mb-3">📋</div>
                                <h4 className="text-base font-bold text-white mb-1">Nenhum laudo técnico localizado</h4>
                                <p className="text-xs text-slate-400 max-w-md mx-auto">
                                    Sua empresa ainda não possui laudos ou pareceres técnicos formalizados no sistema.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-3xl border border-slate-800 bg-slate-900/60 shadow-xl">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-900 border-b border-slate-800 text-[11px] font-black uppercase tracking-wider text-slate-400">
                                            <th className="p-5 w-24">OS</th>
                                            <th className="p-5">Título / Equipamento</th>
                                            <th className="p-5 w-32">Data</th>
                                            <th className="p-5 w-40">Custo Estimado</th>
                                            <th className="p-5 w-48">Status / Resposta</th>
                                            <th className="p-5 text-right w-36">Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800/60 text-sm">
                                        {reports
                                            .filter(r => {
                                                if (!reportSearchTerm.trim()) return true;
                                                const term = reportSearchTerm.toLowerCase();
                                                return (r.title && r.title.toLowerCase().includes(term)) ||
                                                    (r.osNumber && String(r.osNumber).includes(term)) ||
                                                    (r.hostname && r.hostname.toLowerCase().includes(term)) ||
                                                    (r.patrimony && r.patrimony.toLowerCase().includes(term));
                                            })
                                            .map(report => (
                                                <tr 
                                                    key={report.id}
                                                    onClick={() => {
                                                        setSelectedReport(report);
                                                        setIsPreviewReportOpen(true);
                                                    }}
                                                    className="hover:bg-slate-800/40 transition-colors cursor-pointer group"
                                                >
                                                    <td className="p-5 font-mono font-bold text-cyan-400">
                                                        A{report.osNumber || '—'}
                                                    </td>
                                                    <td className="p-5">
                                                        <span className="font-bold text-white group-hover:text-cyan-300 transition-colors block text-base line-clamp-1">
                                                            {report.title || 'Laudo Técnico Sem Título'}
                                                        </span>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            {report.hostname && (
                                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 font-mono">
                                                                    💻 {report.hostname}
                                                                </span>
                                                            )}
                                                            {report.patrimony && (
                                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 font-mono">
                                                                    📌 {report.patrimony}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-5 text-xs text-slate-400 font-mono">
                                                        {report.createdAt?.toDate ? report.createdAt.toDate().toLocaleDateString('pt-BR') : '—'}
                                                    </td>
                                                    <td className="p-5">
                                                        {report.cost ? (
                                                            <span className="font-black text-amber-400">{report.cost}</span>
                                                        ) : (
                                                            <span className="text-slate-500 italic text-xs">Não definido</span>
                                                        )}
                                                    </td>
                                                    <td className="p-5">
                                                        {report.clientResponse ? (
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-slate-800 text-slate-300 border border-slate-700">
                                                                {report.clientResponse}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-slate-500 italic whitespace-nowrap">Status não informado</span>
                                                        )}
                                                    </td>
                                                    <td className="p-5 text-right">
                                                        <button 
                                                            className="px-3.5 py-1.5 bg-cyan-950 hover:bg-cyan-900 text-cyan-400 rounded-lg text-xs font-bold border border-cyan-800 transition-all"
                                                        >
                                                            Visualizar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ABA 5: DISPOSITIVOS MONITORADOS (APENAS USER COMPANY) */}
                {activeTab === 'assets' && portalUser.role === 'User Company' && (
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden min-h-[800px] flex flex-col relative w-full flex-1">
                        <AssetManagementPage 
                            permissionLevel="read" 
                            allowedCompanies={[portalUser.companyId, portalUser.companyName].filter(Boolean)} 
                            uiItems={{ showClientList: false }}
                        />
                    </div>
                )}

                {/* ABA 6: INVENTÁRIO & EQUIPAMENTOS REGISTRADOS AGRUPADOS (PADRÃO INTRANET ARCUS) */}
                {activeTab === 'inventory' && portalUser.role === 'User Company' && (
                    <div className="space-y-6">
                        <div className="bg-slate-900 border border-slate-800 p-6 sm:p-8 rounded-3xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h3 className="text-2xl font-black text-white">Inventário de Equipamentos</h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    Consulte os equipamentos cadastrados no inventário da empresa pela equipe técnica.
                                </p>
                            </div>
                            <span className="text-xs font-bold px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full">
                                Apenas Consulta
                            </span>
                        </div>

                        {loadingEquipment ? (
                                    <div className="py-20 text-center text-slate-400 animate-pulse">Carregando inventário de equipamentos...</div>
                                ) : Object.keys(groupedEquipment).length === 0 ? (
                                    <div className="py-16 text-center bg-slate-900 border border-dashed border-slate-800 rounded-3xl p-8">
                                        <div className="text-4xl mb-3">📦</div>
                                        <h4 className="text-base font-bold text-white mb-1">Nenhum equipamento cadastrado</h4>
                                        <p className="text-xs text-slate-400 max-w-md mx-auto">
                                            Os servidores, roteadores, switches e demais ativos de rede registrados pela equipe técnica aparecerão aqui.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-8">
                                        {Object.keys(groupedEquipment).sort().map(type => {
                                            const filteredItems = getFilteredEquipmentItems(type);
                                            const totalCount = (groupedEquipment[type] || []).length;
                                            const queryVal = equipmentSearchQueries[type] || '';
                                            const cfg = EQUIPMENT_CONFIG[type] || {};

                                            return (
                                                <div 
                                                    key={type} 
                                                    className="bg-slate-900/90 rounded-[2rem] border border-slate-800 flex flex-col h-[520px] overflow-hidden transition-all hover:border-cyan-500/40 hover:shadow-2xl hover:shadow-cyan-950/20 group/card shadow-xl"
                                                >
                                                    {/* Cabeçalho da Categoria */}
                                                    <div className="p-6 border-b border-slate-800/80 bg-slate-900/60 flex items-center justify-between gap-3">
                                                        <h2 className="text-lg font-black text-cyan-300 truncate flex items-center gap-3 shrink-0">
                                                            <div className="text-cyan-400 opacity-70 group-hover/card:opacity-100 transition-opacity">
                                                                {cfg.icon || (
                                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                            {type}
                                                        </h2>
                                                        
                                                        {/* Input de Pesquisa Individual da Coluna */}
                                                        <div className="relative flex-grow max-w-[170px]">
                                                            <input 
                                                                type="text"
                                                                placeholder="Pesquisar..."
                                                                value={queryVal}
                                                                onChange={(e) => setEquipmentSearchQueries(prev => ({ ...prev, [type]: e.target.value }))}
                                                                className="w-full bg-slate-950/80 border border-slate-800 hover:border-cyan-500/40 focus:border-cyan-500 rounded-lg py-1.5 pl-3 pr-7 text-xs text-white placeholder-slate-500 focus:outline-none transition-all"
                                                            />
                                                            {queryVal && (
                                                                <button 
                                                                    onClick={() => setEquipmentSearchQueries(prev => ({ ...prev, [type]: '' }))}
                                                                    className="absolute inset-y-0 right-0 pr-2 flex items-center text-slate-500 hover:text-white"
                                                                >
                                                                    ✕
                                                                </button>
                                                            )}
                                                        </div>

                                                        {/* Badge de Quantidade */}
                                                        <span className="bg-slate-950 text-cyan-400 text-[10px] font-black px-2.5 py-1 rounded-lg border border-cyan-500/20 shadow-inner shrink-0 font-mono">
                                                            {queryVal ? `${filteredItems.length}/${totalCount}` : totalCount} ITENS
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Lista com Scroll Interno dos Itens da Categoria */}
                                                    <div className="flex-grow overflow-y-auto p-5 custom-scrollbar bg-slate-950/20 space-y-3">
                                                        {filteredItems.length === 0 ? (
                                                            <div className="text-center py-14 text-slate-500 text-xs font-bold uppercase tracking-wider">
                                                                Nenhum item encontrado
                                                            </div>
                                                        ) : (
                                                            filteredItems.map((item) => (
                                                                <div 
                                                                    key={item.id}
                                                                    onClick={() => setSelectedEquipmentItem(item)}
                                                                    className="group/item relative bg-slate-900/90 border border-slate-800 p-4 rounded-2xl cursor-pointer transition-all hover:bg-slate-850 hover:border-cyan-500/50 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/40 active:scale-[0.99]"
                                                                >
                                                                    <div className="flex items-center justify-between gap-4">
                                                                        <div className="flex flex-col gap-1 min-w-0">
                                                                            <span className="text-slate-100 font-extrabold truncate text-sm leading-tight group-hover/item:text-cyan-200 transition-colors">
                                                                                {item.data?.nome || 'Sem Nome'}
                                                                            </span>
                                                                            {item.data?.ip && (
                                                                                <span className="text-[10px] text-slate-400 font-mono tracking-wider font-bold group-hover/item:text-cyan-400/80 transition-colors uppercase">
                                                                                    IP: {item.data.ip}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="bg-slate-800 p-2 rounded-xl text-slate-500 group-hover/item:text-cyan-400 group-hover/item:bg-cyan-500/10 transition-all border border-transparent group-hover/item:border-cyan-500/20 shrink-0">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                    </div>
                )}
            </main>

            {/* MODAL DE NOVO CHAMADO */}
            {isNewTicketOpen && (
                <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex justify-center items-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-slate-900 border border-slate-700/80 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                        <header className="p-6 border-b border-slate-800 bg-slate-800/30 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-black text-white">Abrir Novo Chamado</h3>
                                <p className="text-xs text-slate-400 mt-0.5">Nossa equipe de suporte receberá sua solicitação imediatamente.</p>
                            </div>
                            <button onClick={() => setIsNewTicketOpen(false)} className="text-slate-400 hover:text-white p-2 rounded-xl">
                                ✕
                            </button>
                        </header>

                        <form onSubmit={handleCreateTicket} className="p-6 overflow-y-auto space-y-4 custom-scrollbar">
                            {ticketFeedback.msg && (
                                <div className={`p-4 rounded-xl text-xs font-bold ${
                                    ticketFeedback.type === 'error' ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                                }`}>
                                    {ticketFeedback.msg}
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                    Título / Assunto do Problema *
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Ex: Computador não liga / Impressora sem rede"
                                    value={ticketTitle}
                                    onChange={e => setTicketTitle(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm focus:border-cyan-500 focus:outline-none"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                        Empresa
                                    </label>
                                    <input
                                        type="text"
                                        disabled
                                        value={portalUser.companyName}
                                        className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-slate-400 text-sm cursor-not-allowed"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                        Solicitante
                                    </label>
                                    <input
                                        type="text"
                                        disabled
                                        value={`${portalUser.name} (${portalUser.email})`}
                                        className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-slate-400 text-sm cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            {/* Seleção do Tipo de Contrato / Atendimento (Dropdown Único) */}
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                                    <span>Vínculo com Contrato / Serviço *</span>
                                    {contracts.length > 0 && (
                                        <span className="text-[10px] text-cyan-400 font-semibold lowercase">
                                            {contracts.length} contrato(s) ativo(s)
                                        </span>
                                    )}
                                </label>
                                
                                <select
                                    value={ticketContractId}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setTicketContractId(val);
                                        if (val === 'outros') {
                                            setTicketContractName('Outros');
                                        } else {
                                            const found = contracts.find(c => c.id === val);
                                            setTicketContractName(found?.contractName || found?.templateName || found?.name || 'Contrato de Suporte');
                                        }
                                    }}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white text-sm focus:border-cyan-500 focus:outline-none cursor-pointer"
                                >
                                    {contracts.map(c => {
                                        const title = c.contractName || c.templateName || c.name || 'Contrato de Suporte';
                                        return (
                                            <option key={c.id} value={c.id}>
                                                📄 {title}
                                            </option>
                                        );
                                    })}
                                    <option value="outros">
                                        ⚡ Outros / Avulso (Sem vínculo com contrato)
                                    </option>
                                </select>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    {ticketContractId === 'outros' 
                                        ? 'Chamado sem cobertura de contrato recorrente ou para serviços avulsos.' 
                                        : 'O atendimento será direcionado e registrado no escopo do contrato selecionado.'}
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                    Prioridade Estimada
                                </label>
                                <select
                                    value={ticketPriority}
                                    onChange={e => setTicketPriority(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-cyan-500 focus:outline-none cursor-pointer"
                                >
                                    <option value="low">Baixa (Dúvida ou solicitação simples)</option>
                                    <option value="medium">Média (Impacto parcial no trabalho)</option>
                                    <option value="high">Alta (Máquina parada ou urgência)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                    Descrição Detalhada do Problema *
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    placeholder="Descreva o que está ocorrendo, quando começou e qualquer detalhe que ajude os técnicos a resolverem mais rápido..."
                                    value={ticketDescription}
                                    onChange={e => setTicketDescription(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 text-white text-sm focus:border-cyan-500 focus:outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                    Anexar Fotos / Arquivos (Opcional)
                                </label>
                                <input
                                    type="file"
                                    multiple
                                    onChange={e => setSelectedFiles(Array.from(e.target.files))}
                                    className="w-full text-xs text-slate-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-800 file:text-cyan-400 hover:file:bg-slate-700 cursor-pointer"
                                />
                                {selectedFiles.length > 0 && (
                                    <p className="text-[11px] text-cyan-400 mt-1 font-medium">
                                        {selectedFiles.length} arquivo(s) selecionado(s)
                                    </p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setIsNewTicketOpen(false)}
                                    className="px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs uppercase tracking-wider transition-all"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={submittingTicket}
                                    className="px-8 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-900/40 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {submittingTicket ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                                            <span>Enviando...</span>
                                        </>
                                    ) : (
                                        'Criar Chamado'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DRAWER / MODAL DE DETALHES DO CHAMADO */}
            {selectedTicket && (
                <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex justify-end z-50 animate-in fade-in duration-200">
                    <div className="bg-slate-900 border-l border-slate-800 w-full max-w-2xl h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-300">
                        {/* Header do Chamado */}
                        <header className="p-6 border-b border-slate-800 bg-slate-800/30 flex justify-between items-start gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="font-mono font-black text-cyan-400 text-lg">
                                        A{selectedTicket.numeroSequencial || '—'}
                                    </span>
                                    <span className="px-3 py-0.5 rounded-full text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                                        {selectedTicket.status || 'Novo Chamado'}
                                    </span>
                                    {selectedTicket.assignedToEmail && selectedTicket.assignedToEmail.length > 0 ? (
                                        <span className="px-3 py-0.5 rounded-full text-xs font-bold bg-cyan-950/60 text-cyan-300 border border-cyan-700/50 flex items-center gap-1">
                                            👤 Técnico: {selectedTicket.assignedToEmail.map(e => e.split('@')[0]).join(', ')}
                                        </span>
                                    ) : (
                                        <span className="px-3 py-0.5 rounded-full text-xs font-semibold bg-slate-800 text-slate-400 border border-slate-700">
                                            Sem técnico atribuído
                                        </span>
                                    )}
                                    {selectedTicket.contractName && (
                                        <span className={`px-3 py-0.5 rounded-full text-xs font-bold border flex items-center gap-1 ${
                                            selectedTicket.contractName === 'Outros' 
                                                ? 'bg-amber-950/40 text-amber-400 border-amber-800/40' 
                                                : 'bg-emerald-950/40 text-emerald-400 border-emerald-800/40'
                                        }`}>
                                            📄 Contrato: {selectedTicket.contractName}
                                        </span>
                                    )}
                                </div>
                                <h3 className="text-xl font-black text-white">{selectedTicket.title}</h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    Aberto em {selectedTicket.createdAt?.toDate ? selectedTicket.createdAt.toDate().toLocaleString('pt-BR') : '—'} por {selectedTicket.clientName}
                                </p>
                            </div>
                            <button
                                onClick={() => setSelectedTicket(null)}
                                className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors"
                            >
                                ✕
                            </button>
                        </header>

                        {/* Corpo com Descrição e Mensagens */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                            {/* Card Descrição Inicial */}
                            <div className="bg-slate-800/40 p-5 rounded-2xl border border-slate-800 space-y-3">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Descrição do Problema</span>
                                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                                    {selectedTicket.description}
                                </p>
                                {selectedTicket.attachments?.length > 0 && (
                                    <div className="pt-3 border-t border-slate-700/50">
                                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2">Anexos Iniciais:</span>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedTicket.attachments.map((att, idx) => (
                                                <a 
                                                    key={idx}
                                                    href={att.url} 
                                                    target="_blank" 
                                                    rel="noreferrer"
                                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-cyan-400 text-xs font-semibold rounded-lg border border-slate-700 flex items-center gap-1.5 transition-colors"
                                                >
                                                    📎 {att.name || `Anexo ${idx + 1}`}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Linha do Tempo de Respostas */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Histórico de Mensagens</h4>

                                {comments.length === 0 ? (
                                    <p className="text-xs text-slate-500 italic">Nenhuma resposta registrada ainda. Você pode enviar uma mensagem abaixo.</p>
                                ) : (
                                    comments.map(c => {
                                        const isClientMsg = c.isPortalUser || c.userEmail === portalUser.email;
                                        const techDisplayName = c.userName || (c.userEmail ? c.userEmail.split('@')[0] : 'Técnico Arcus');
                                        return (
                                            <div 
                                                key={c.id} 
                                                className={`p-4 rounded-2xl border ${
                                                    isClientMsg 
                                                        ? 'bg-cyan-950/20 border-cyan-800/40 ml-4' 
                                                        : 'bg-slate-800/40 border-slate-700/60 mr-4'
                                                }`}
                                            >
                                                <div className="flex justify-between items-center mb-1.5">
                                                    <span className={`text-xs font-bold ${isClientMsg ? 'text-cyan-400' : 'text-amber-400'}`}>
                                                        {isClientMsg ? (c.userName || 'Você') : `👨‍💻 Técnico: ${techDisplayName}`}
                                                    </span>
                                                    <span className="text-[10px] text-slate-500 font-mono">
                                                        {c.createdAt?.toDate ? c.createdAt.toDate().toLocaleString('pt-BR') : '—'}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                                                    {c.text}
                                                </p>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Formulário de Envio de Mensagem */}
                        <footer className="p-4 border-t border-slate-800 bg-slate-900">
                            <form onSubmit={handleSendComment} className="space-y-3">
                                <textarea
                                    rows={2}
                                    placeholder="Escreva uma mensagem ou resposta para a equipe técnica..."
                                    value={newComment}
                                    onChange={e => setNewComment(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-white text-xs focus:border-cyan-500 focus:outline-none"
                                />

                                <div className="flex justify-between items-center gap-3">
                                    <input 
                                        type="file" 
                                        multiple 
                                        onChange={e => setCommentFiles(Array.from(e.target.files))}
                                        className="text-[11px] text-slate-400 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-[11px] file:font-bold file:bg-slate-800 file:text-cyan-400 hover:file:bg-slate-700 cursor-pointer"
                                    />
                                    <button
                                        type="submit"
                                        disabled={sendingComment || (!newComment.trim() && commentFiles.length === 0)}
                                        className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-lg transition-all disabled:opacity-50"
                                    >
                                        {sendingComment ? 'Enviando...' : 'Enviar Resposta'}
                                    </button>
                                </div>
                            </form>
                        </footer>
                    </div>
                </div>
            )}
            {/* MODAL / VISUALIZADOR DE LAUDO TÉCNICO (COM IMPRESSÃO) */}
            {isPreviewReportOpen && selectedReport && (
                <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex flex-col items-center z-[150] overflow-y-auto p-4 sm:p-6 animate-in fade-in duration-200">
                    <div className="sticky top-0 w-full max-w-4xl bg-slate-900/90 backdrop-blur-md border border-slate-700/80 rounded-2xl p-4 mb-4 flex justify-between items-center z-10 shadow-2xl">
                        <div className="flex items-center gap-3">
                            <span className="font-mono text-xs font-black text-cyan-400 bg-cyan-950/60 px-3 py-1 rounded-lg border border-cyan-800/40">
                                OS: A{selectedReport.osNumber || '—'}
                            </span>
                            <span className="text-white font-bold text-sm hidden sm:inline">{selectedReport.title}</span>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsPreviewReportOpen(false)}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-xs transition-colors border border-slate-700"
                            >
                                Fechar
                            </button>
                            <button
                                onClick={() => window.print()}
                                className="px-5 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-black rounded-xl text-xs transition-all shadow-lg shadow-cyan-900/40 flex items-center gap-2"
                            >
                                🖨️ Imprimir Laudo
                            </button>
                        </div>
                    </div>

                    {/* Folha do Laudo */}
                    <div className="bg-white text-slate-900 w-full max-w-4xl rounded-2xl p-8 sm:p-12 shadow-2xl space-y-8 my-auto print:m-0 print:p-8 print:shadow-none print:max-w-none print:w-full">
                        {/* Header do Laudo */}
                        <div className="flex justify-between items-start border-b-2 border-slate-200 pb-6 gap-4">
                            <div>
                                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 uppercase tracking-tight">
                                    {selectedReport.title || 'Laudo Técnico'}
                                </h2>
                                <p className="text-sm font-bold text-cyan-600 tracking-wider uppercase mt-1">
                                    Arcus Tecnologia • Parecer Técnico Especializado
                                </p>
                            </div>
                            <div className="text-right">
                                <span className="text-xl font-black font-mono text-cyan-600 block">
                                    A{selectedReport.osNumber || '—'}
                                </span>
                                <span className="text-xs text-slate-500">
                                    {selectedReport.createdAt?.toDate ? selectedReport.createdAt.toDate().toLocaleDateString('pt-BR') : '—'}
                                </span>
                            </div>
                        </div>

                        {/* Informações Básicas do Cliente e Equipamento */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                            <div>
                                <span className="font-bold text-slate-400 uppercase tracking-wider block mb-1">Cliente / Empresa</span>
                                <span className="font-black text-slate-800 text-sm">{selectedReport.clientName}</span>
                            </div>
                            <div>
                                <span className="font-bold text-slate-400 uppercase tracking-wider block mb-1">Ordem de Serviço</span>
                                <span className="font-black text-slate-800 font-mono text-sm">A{selectedReport.osNumber || '—'}</span>
                            </div>
                            {selectedReport.hostname && (
                                <div>
                                    <span className="font-bold text-slate-400 uppercase tracking-wider block mb-1">Hostname / Dispositivo</span>
                                    <span className="font-black text-slate-800 font-mono text-sm">{selectedReport.hostname}</span>
                                </div>
                            )}
                            {selectedReport.patrimony && (
                                <div>
                                    <span className="font-bold text-slate-400 uppercase tracking-wider block mb-1">Patrimônio</span>
                                    <span className="font-black text-slate-800 font-mono text-sm">{selectedReport.patrimony}</span>
                                </div>
                            )}
                        </div>

                        {/* Problema Relatado */}
                        <div className="space-y-2">
                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-1">
                                Problema Relatado
                            </h4>
                            <div 
                                className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{ __html: selectedReport.problemDescription || 'Não informado.' }}
                            />
                        </div>

                        {/* Análise Técnica e Constatações */}
                        <div className="space-y-2">
                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-1">
                                Análise Técnica e Constatações
                            </h4>
                            <div 
                                className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{ __html: selectedReport.technicalAnalysis || selectedReport.technicalDiagnosis || 'Não informado.' }}
                            />
                        </div>

                        {/* Diagnóstico Final */}
                        <div className="space-y-2">
                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-1">
                                Diagnóstico
                            </h4>
                            <div 
                                className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{ __html: selectedReport.diagnosis || 'Não informado.' }}
                            />
                        </div>

                        {/* Valores e Parecer */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                            <div>
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-xs block mb-1">Custo Estimado de Reparo/Serviço</span>
                                <span className="font-black text-2xl text-slate-900">{selectedReport.cost || 'R$ 0,00'}</span>
                            </div>
                            <div>
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-xs block mb-1">Status / Posição do Cliente</span>
                                <span className="font-bold text-slate-800 text-sm block mt-1">{selectedReport.clientResponse || 'Pendente de resposta'}</span>
                            </div>
                        </div>

                        {/* Referências de Preços / Justificativa se houver */}
                        {selectedReport.referenceLinks && selectedReport.referenceLinks.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-1">
                                    Referências de Preços / Peças
                                </h4>
                                <div className="flex flex-wrap gap-2 pt-1">
                                    {selectedReport.referenceLinks.map((link, idx) => (
                                        <a
                                            key={idx}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-cyan-700 text-xs font-bold rounded-lg border border-slate-300 transition-colors flex items-center gap-1"
                                        >
                                            🔗 {link.label || `Link ${idx + 1}`}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Rodapé / Assinatura do Laudo */}
                        <div className="pt-8 border-t-2 border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-500">
                            <div>
                                <p className="font-bold text-slate-700">Responsável Técnico Arcus</p>
                                <p className="font-mono text-[11px]">{selectedReport.createdBy || 'Equipe Arcus Tecnologia'}</p>
                            </div>
                            <div className="text-center sm:text-right">
                                <p className="font-bold text-slate-700">Documento Eletrônico de Infraestrutura</p>
                                <p className="text-[10px] text-slate-400 font-mono">ID: {selectedReport.id}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE DETALHES DE EQUIPAMENTO DO INVENTÁRIO */}
            {selectedEquipmentItem && (
                <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex justify-center items-center z-[150] p-4 animate-in fade-in duration-200">
                    <div className="bg-slate-900 border border-slate-700/80 rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                        <header className="p-6 border-b border-slate-800 bg-slate-800/40 flex justify-between items-start gap-4">
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg bg-cyan-950/60 text-cyan-400 border border-cyan-800/40">
                                    {selectedEquipmentItem.type || 'Equipamento'}
                                </span>
                                <h3 className="text-2xl font-black text-white mt-2">
                                    {selectedEquipmentItem.data?.nome || 'Equipamento Sem Nome'}
                                </h3>
                            </div>
                            <button
                                onClick={() => setSelectedEquipmentItem(null)}
                                className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition-colors"
                            >
                                ✕
                            </button>
                        </header>

                        <div className="p-6 overflow-y-auto space-y-4 custom-scrollbar bg-slate-950/30">
                            {/* Campos padrões configurados para o tipo */}
                            <div className="grid grid-cols-1 gap-3">
                                {EQUIPMENT_CONFIG[selectedEquipmentItem.type]?.fields
                                    .filter(f => selectedEquipmentItem.data?.[f.name] && f.name !== 'nome' && f.name !== 'totpSecret')
                                    .map(field => {
                                        const val = selectedEquipmentItem.data[field.name];
                                        const isPassword = field.type === 'password' || field.name === 'senha';
                                        return (
                                            <div key={field.name} className="bg-slate-900/80 p-4 rounded-2xl border border-slate-800">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                                    {field.label || field.name}
                                                </span>
                                                <div className="text-sm font-bold text-white">
                                                    {isPassword ? (
                                                        <PasswordDisplay value={val} />
                                                    ) : (
                                                        <LinkifyText text={String(val)} />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>

                            {/* Autenticação em Duas Etapas (2FA / TOTP) */}
                            {selectedEquipmentItem.data?.totpSecret && (
                                <div className="bg-slate-900/90 p-4 rounded-2xl border border-cyan-500/30 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-black uppercase tracking-wider text-cyan-400">
                                            Autenticação 2FA (Código Temporário)
                                        </span>
                                    </div>
                                    <div className="bg-slate-950/60 p-3 rounded-xl border border-slate-800">
                                        <TOTPDisplay secret={selectedEquipmentItem.data.totpSecret} />
                                    </div>
                                </div>
                            )}

                            {/* Campos customizados adicionais se houver */}
                            {selectedEquipmentItem.data?.customFields && selectedEquipmentItem.data.customFields.length > 0 && (
                                <div className="pt-4 border-t border-slate-800 space-y-3">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider">Informações Adicionais</h4>
                                    <div className="grid grid-cols-1 gap-3">
                                        {selectedEquipmentItem.data.customFields.map((cf, idx) => cf.name && (
                                            <div key={idx} className="bg-slate-900/80 p-4 rounded-2xl border border-slate-800">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                                                    {cf.name}
                                                </span>
                                                <span className="text-sm font-bold text-white">
                                                    <LinkifyText text={String(cf.value || '')} />
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <footer className="p-4 border-t border-slate-800 bg-slate-900 flex justify-end">
                            <button
                                onClick={() => setSelectedEquipmentItem(null)}
                                className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs uppercase tracking-wider rounded-xl border border-slate-700 transition-colors"
                            >
                                Fechar Detalhes
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {/* MODAL DASHBOARD DE ESTATÍSTICAS (ÁREA DO CLIENTE) */}
            {isDashboardOpen && (
                <div className="fixed inset-0 bg-black/85 flex justify-center items-center z-[130] p-4 font-sans backdrop-blur-sm animate-fadeIn">
                    <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden font-sans">
                        <header className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-800/80 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <svg className="h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <h3 className="font-bold text-xl text-cyan-400 uppercase tracking-widest">Dashboard de Estatísticas</h3>
                            </div>
                            <button 
                                onClick={() => setIsDashboardOpen(false)} 
                                className="text-slate-400 hover:text-white transition-colors text-2xl font-bold"
                            >
                                ✕
                            </button>
                        </header>

                        <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar bg-slate-900/10 flex-grow font-sans">
                            {/* Linha 1: Cards Rápidos */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 shadow-md">
                                    <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold block">Total de Chamados</span>
                                    <span className="text-3xl font-black text-white mt-1 block">{dashboardStats.total}</span>
                                </div>
                                <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 shadow-md">
                                    <span className="text-[10px] text-cyan-400 uppercase tracking-wider font-bold block">Chamados Ativos</span>
                                    <span className="text-3xl font-black text-cyan-300 mt-1 block">{dashboardStats.active}</span>
                                </div>
                                <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 shadow-md">
                                    <span className="text-[10px] text-emerald-400 uppercase tracking-wider font-bold block">Chamados Finalizados</span>
                                    <span className="text-3xl font-black text-emerald-400 mt-1 block">{dashboardStats.finished}</span>
                                </div>
                                <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 shadow-md">
                                    <span className="text-[10px] text-orange-400 uppercase tracking-wider font-bold block">Chamados Agendados</span>
                                    <span className="text-3xl font-black text-orange-400 mt-1 block">{dashboardStats.scheduled}</span>
                                </div>
                            </div>

                            {/* Linha 2: Distribuição */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Por Status */}
                                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700 shadow-md">
                                    <h4 className="text-xs text-slate-400 uppercase font-black tracking-wider mb-4">Por Status</h4>
                                    <div className="space-y-3">
                                        {Object.entries(dashboardStats.statusCounts).length === 0 ? (
                                            <p className="text-xs text-slate-500 italic">Nenhum chamado registrado.</p>
                                        ) : (
                                            Object.entries(dashboardStats.statusCounts).sort((a,b) => b[1] - a[1]).map(([status, count]) => {
                                                const pct = dashboardStats.total > 0 ? ((count / dashboardStats.total) * 100).toFixed(1) : 0;
                                                const statusColor = statuses.find(s => s.name === status)?.color || '#60a5fa';
                                                return (
                                                    <div key={status} className="space-y-1">
                                                        <div className="flex justify-between text-xs text-slate-300">
                                                            <span className="font-semibold truncate max-w-[150px]" title={status}>{status}</span>
                                                            <span>{count} ({pct}%)</span>
                                                        </div>
                                                        <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden">
                                                            <div style={{ width: `${pct}%`, backgroundColor: statusColor }} className="h-full"></div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Por Técnico */}
                                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700 shadow-md">
                                    <h4 className="text-xs text-slate-400 uppercase font-black tracking-wider mb-4">Por Técnico</h4>
                                    <div className="space-y-3">
                                        {Object.entries(dashboardStats.techCounts).length === 0 ? (
                                            <p className="text-xs text-slate-500 italic">Nenhum técnico atribuído.</p>
                                        ) : (
                                            Object.entries(dashboardStats.techCounts).sort((a,b) => b[1] - a[1]).map(([tech, count]) => {
                                                const pct = dashboardStats.total > 0 ? ((count / dashboardStats.total) * 100).toFixed(1) : 0;
                                                return (
                                                    <div key={tech} className="space-y-1">
                                                        <div className="flex justify-between text-xs text-slate-300">
                                                            <span className="font-semibold capitalize truncate max-w-[150px]" title={tech}>{tech}</span>
                                                            <span>{count} chamados</span>
                                                        </div>
                                                        <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden">
                                                            <div style={{ width: `${pct}%` }} className="bg-cyan-500 h-full"></div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Top Solicitantes */}
                                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700 shadow-md">
                                    <h4 className="text-xs text-slate-400 uppercase font-black tracking-wider mb-4">
                                        {portalUser?.role === 'User Company' ? 'Top Solicitantes' : 'Solicitante'}
                                    </h4>
                                    <div className="space-y-3">
                                        {Object.entries(dashboardStats.requesterCounts).length === 0 ? (
                                            <p className="text-xs text-slate-500 italic">Nenhum chamado registrado.</p>
                                        ) : (
                                            Object.entries(dashboardStats.requesterCounts).sort((a,b) => b[1] - a[1]).slice(0, 5).map(([requester, count]) => {
                                                const pct = dashboardStats.total > 0 ? ((count / dashboardStats.total) * 100).toFixed(1) : 0;
                                                return (
                                                    <div key={requester} className="space-y-1">
                                                        <div className="flex justify-between text-xs text-slate-300">
                                                            <span className="font-semibold truncate max-w-[150px]" title={requester}>{requester}</span>
                                                            <span>{count} chamados</span>
                                                        </div>
                                                        <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden">
                                                            <div style={{ width: `${pct}%` }} className="bg-teal-500 h-full"></div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <footer className="p-4 border-t border-slate-700 bg-slate-800 flex justify-end flex-shrink-0">
                            <button 
                                onClick={() => setIsDashboardOpen(false)} 
                                className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-5 rounded-lg text-xs transition-colors font-sans"
                            >
                                Fechar
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {isUsersModalOpen && companyData && (
                <ClientPortalUsersModal
                    client={{ id: portalUser.companyId, name: portalUser.companyName, ...companyData }}
                    collectionName="clients"
                    onClose={() => setIsUsersModalOpen(false)}
                    permissionLevel="edit"
                    isPortalUser={true}
                />
            )}

            {/* MODAL DE ASSINATURA ELETRÔNICA DO CLIENTE (PORTAL) */}
            <SignatureModal
                isOpen={isSignContractModalOpen}
                onClose={() => {
                    if (!isSubmittingSignature) {
                        setIsSignContractModalOpen(false);
                    }
                }}
                onConfirmSignature={handleSignContractClient}
                signerRole="client"
                signerName={portalUser?.name || ''}
                signerEmail={portalUser?.email || ''}
                contractTitle={selectedContract?.contractName || selectedContract?.templateName || 'Contrato de TI'}
                clientName={portalUser?.companyName || selectedContract?.clientName || 'Cliente'}
                contractVersion={selectedVersion?.versionNumber || selectedContract?.currentVersion || 1}
                isSubmitting={isSubmittingSignature}
            />
        </div>
    );
}
